import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0caad674-da07-42ae-8497-83c3015cb5cd/artifacts/d09l0qqk_AEE_logo_image.svg';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', href: '#hero' },
    { name: 'Services', href: '#services' },
    { name: 'Industries', href: '#industries' },
    { name: 'Projects', href: '#projects' },
    { name: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    if (href === '#hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'py-3' : 'py-5'
      }`}
      style={{
        background: isScrolled 
          ? 'rgba(255, 255, 255, 0.85)' 
          : 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        borderBottom: isScrolled 
          ? '1px solid rgba(28, 75, 138, 0.08)' 
          : '1px solid transparent',
        boxShadow: isScrolled 
          ? '0 4px 30px rgba(0, 0, 0, 0.06)'
          : 'none'
      }}
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.a 
            href="/" 
            className="flex items-center gap-4 group"
            whileHover={{ scale: 1.02 }}
          >
            <img 
              src={LOGO_URL}
              alt="AEE Logo"
              className="h-16 w-auto"
            />
            <div className="hidden sm:block">
              <span className="text-xl font-bold text-[#1a1a2e] leading-tight block">Advanced Engineered</span>
              <span className="text-xl font-bold text-[#1a1a2e] leading-tight block">Environments</span>
            </div>
          </motion.a>

          {/* Desktop Nav - Glass Pill */}
          <nav 
            className="hidden lg:flex items-center gap-1 p-1.5 rounded-full"
            style={{
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(28, 75, 138, 0.08)',
              boxShadow: '0 2px 15px rgba(0, 0, 0, 0.04)'
            }}
          >
            {navLinks.map((link) => (
              <motion.a
                key={link.name}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="px-5 py-2.5 text-sm font-medium text-[#3c3c43] hover:text-[#1C4B8A] transition-all rounded-full hover:bg-white/80"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {link.name}
              </motion.a>
            ))}
          </nav>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center gap-3">
            <motion.a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="hidden md:flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-full transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #1C4B8A 0%, #153A6B 100%)',
                boxShadow: '0 4px 20px rgba(28, 75, 138, 0.35)'
              }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Quote
              <ArrowRight size={14} />
            </motion.a>

            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-3 rounded-xl"
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(28, 75, 138, 0.1)',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)'
              }}
              whileTap={{ scale: 0.95 }}
            >
              {isMobileMenuOpen ? <X size={20} className="text-[#1a1a2e]" /> : <Menu size={20} className="text-[#1a1a2e]" />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <nav 
                className="py-4 mt-4 space-y-2 rounded-2xl p-4"
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(28, 75, 138, 0.08)',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)'
                }}
              >
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="block px-4 py-3 text-sm font-medium text-[#3c3c43] rounded-xl hover:bg-[#1C4B8A]/5 hover:text-[#1C4B8A] transition-all"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    {link.name}
                  </motion.a>
                ))}
                <motion.a
                  href="#contact"
                  onClick={(e) => scrollToSection(e, '#contact')}
                  className="block mt-4 px-5 py-4 text-center text-white font-semibold rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, #1C4B8A 0%, #153A6B 100%)',
                    boxShadow: '0 4px 20px rgba(28, 75, 138, 0.3)'
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  Request Quote
                </motion.a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
